import React, { useCallback } from 'react';
import {
  Box, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, InputAdornment, Chip,
} from '@mui/material';
import { Search, Clear, Add } from '@mui/icons-material';
import { useTenders } from '../../hooks/TenderContext';
import { useAuth } from '../../hooks/AuthContext';
import { STATUSES, FIELDS, WIN_STATUSES, PRIORITIES } from '../../utils/constants';
import { debounce } from '../../utils/helpers';

const FilterBar = () => {
  const { filters, setFilters, clearFilters, openCreateForm } = useTenders();
  const { canCreate } = useAuth();

  const hasFilters = Object.values(filters).some(v => v);

  const handleSearchChange = useCallback(
    debounce((value) => {
      setFilters(prev => ({ ...prev, search: value }));
    }, 300),
    []
  );

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField
          placeholder="חיפוש חופשי..."
          size="small"
          defaultValue={filters.search}
          onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>סטטוס</InputLabel>
          <Select value={filters.status} label="סטטוס" onChange={handleFilterChange('status')}>
            <MenuItem value="">הכל</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>תחום</InputLabel>
          <Select value={filters.field} label="תחום" onChange={handleFilterChange('field')}>
            <MenuItem value="">הכל</MenuItem>
            {FIELDS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>תוצאה</InputLabel>
          <Select value={filters.win_status} label="תוצאה" onChange={handleFilterChange('win_status')}>
            <MenuItem value="">הכל</MenuItem>
            {WIN_STATUSES.map(w => <MenuItem key={w} value={w}>{w}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>תעדוף</InputLabel>
          <Select value={filters.priority} label="תעדוף" onChange={handleFilterChange('priority')}>
            <MenuItem value="">הכל</MenuItem>
            {PRIORITIES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>

        {hasFilters && (
          <Button variant="outlined" size="small" startIcon={<Clear />} onClick={clearFilters} color="inherit">
            נקה סינון
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Only show "New Tender" button for admin / editor */}
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreateForm}
            sx={{ borderRadius: 2, px: 3 }}
          >
            מכרז חדש
          </Button>
        )}
      </Box>

      {hasFilters && (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
          {filters.search && (
            <Chip label={`חיפוש: "${filters.search}"`} size="small" onDelete={() => setFilters(prev => ({ ...prev, search: '' }))} />
          )}
          {filters.status && (
            <Chip label={`סטטוס: ${filters.status}`} size="small" onDelete={() => setFilters(prev => ({ ...prev, status: '' }))} />
          )}
          {filters.field && (
            <Chip label={`תחום: ${filters.field}`} size="small" onDelete={() => setFilters(prev => ({ ...prev, field: '' }))} />
          )}
          {filters.win_status && (
            <Chip label={`תוצאה: ${filters.win_status}`} size="small" onDelete={() => setFilters(prev => ({ ...prev, win_status: '' }))} />
          )}
          {filters.priority && (
            <Chip label={`תעדוף: ${filters.priority}`} size="small" onDelete={() => setFilters(prev => ({ ...prev, priority: '' }))} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default FilterBar;
